import { ErrorAlert } from '@if/mui'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { useFeatureFlags } from '@src/api/app-config-hooks'
import {
  InteracWireSearchPossibleMatchFilters,
  useMatchInteracWire,
  useSearchPossibleMatch,
} from '@src/api/interac-wire-api'
import { useNotification } from '@src/providers/NotificationProvider'
import { InteracPossibleMatch, InteracWire } from '@src/types/interac'
import { format as formatDate, parseISO } from '@if/core/date'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import InteracWireInformation from './InteracWireInformation'
import InteracWireMatchLoanFilters from './InteracWireMatchLoanFilters'
import InteracWirePossibleMatch from './InteracWirePossibleMatch'

interface InteracWireMatchUnknownDialogProps {
  interacWire: InteracWire | null
  onClose: () => void
}

const InteracWireMatchUnknownDialog = ({ interacWire, onClose }: InteracWireMatchUnknownDialogProps) => {
  const { t } = useTranslation()
  const { useLoanDateOverrideInteracTransfers } = useFeatureFlags()
  const [dateOverride, setDateOverride] = useState<Date | null>(null)
  const [filters, setFilters] = useState<InteracWireSearchPossibleMatchFilters | null>(null)
  const [possibleMatches, isFetching, errorSearch] = useSearchPossibleMatch(interacWire?.id ?? null, filters)
  const { mutateAsync: matchInteracWire, isPending, error: errorMatch, reset } = useMatchInteracWire()
  const { showSuccess } = useNotification()

  const handleClose = useCallback(() => {
    if (isPending || isFetching) return
    setFilters(null)
    setDateOverride(null)
    onClose()
    reset()
  }, [isFetching, isPending, onClose, reset])

  const handleMatch = useCallback(
    (possibleMatch: InteracPossibleMatch) => {
      if (!interacWire) return

      void matchInteracWire({
        id: interacWire.id,
        versionTag: interacWire.versionTag,
        loanId: possibleMatch.loanId,
        dateOverride: dateOverride ? formatDate(dateOverride, 'yyyy-MM-dd') : null,
      }).then(() => {
        setFilters(null)
        setDateOverride(null)
        reset()
        onClose()
        showSuccess()
      })
    },
    [interacWire, matchInteracWire, dateOverride, onClose, showSuccess, reset],
  )

  const handleOnFiltersChange = useCallback(
    (newFilters: InteracWireSearchPossibleMatchFilters | null) => {
      setFilters(newFilters)
      reset()
    },
    [reset],
  )

  const possibleMatchesResults = useMemo(() => {
    if (!interacWire || !filters) return null
    return possibleMatches.map((match) => (
      <InteracWirePossibleMatch
        key={match.loanId}
        possibleMatch={match}
        variant="outlined"
        onSelect={handleMatch}
        disabled={isPending}
      />
    ))
  }, [interacWire, filters, possibleMatches, handleMatch, isPending])

  return (
    <Dialog open={Boolean(interacWire)} maxWidth="lg" onClose={handleClose}>
      <DialogTitle>
        <Stack gap={1}>
          <InteracWireInformation interacWire={interacWire} />
          {useLoanDateOverrideInteracTransfers && interacWire && (
            <DatePicker
              label={t('interacWire.dateOverride')}
              value={dateOverride}
              onChange={setDateOverride}
              minDate={parseISO(interacWire.receivedOn)}
              slotProps={{ field: { clearable: true }, textField: { size: 'small' } }}
            />
          )}
          <InteracWireMatchLoanFilters onFiltersChange={handleOnFiltersChange} autofocus />
          {(errorSearch || errorMatch) && <ErrorAlert error={errorSearch || errorMatch} disabledGutterBottom />}
        </Stack>
      </DialogTitle>
      {(isFetching || isPending) && <LinearProgress />}
      <DialogContent dividers>
        {possibleMatches.length === 0 && !isFetching && filters && (
          <Typography textAlign="center">{t('common.noResults')}</Typography>
        )}
        {!filters && <Typography textAlign="center">{t('common.filtersRequired')}</Typography>}
        {possibleMatchesResults}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit" disabled={isPending || isFetching}>
          {t('common.close')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default InteracWireMatchUnknownDialog
